import { computed } from "vue";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { supportsSubtotals, toBarChartType } from "@/composables/useChartRules";

export function useSubtotals(cardMeta, localOptions, updateLocalOption, displayLocale, currentChartType) {
  const isSubtotalsEligible = computed(() => supportsSubtotals(cardMeta.value));

  const hasSubtotals = computed({
    get: () => !!localOptions.value.subtotals_enabled,
    set: (val) => {
      updateLocalOption("subtotals_enabled", val);
      if (!val) return;
      if (!localOptions.value.subtotals?.length) {
        updateLocalOption("subtotals", [{ label: "", codes: [] }]);
      }
      const barType = toBarChartType(currentChartType.value);
      if (barType !== currentChartType.value) updateLocalOption("chart_type", barType);
    },
  });

  const subtotalsList = computed({
    get: () => localOptions.value.subtotals || [],
    set: (val) => updateLocalOption("subtotals", val),
  });

  const subtotalAnswerOptions = computed(() => {
    const meta = cardMeta.value;
    if (!meta) return [];
    const type = meta.type;
    const s = meta.specs || {};
    if (type === "q_matrix") {
      if (s.matrix_type === "rating") return ratingOptions(s);
      if (s.matrix_type === "nps") return rangeOptions(0, 10);
      return answerOptions(s);
    }
    if (["q_single", "q_image_choice", "q_image_choice_multiple"].includes(type)) return answerOptions(s);
    if (type === "q_rating") return ratingOptions(s);
    if (type === "q_slider") return rangeOptions(s.slider_limits?.min ?? 0, s.slider_limits?.max ?? 10);
    if (type === "q_nps") return rangeOptions(0, 10);
    return [];
  });

  function answerOptions(s) {
    return (s.answers || []).map((a, i) => ({
      value: String(i + 1),
      label: resolveLocalizedTitle(a.title, displayLocale.value) || String(i + 1),
    }));
  }

  function ratingOptions(s) {
    return rangeOptions(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
  }

  function rangeOptions(min, max) {
    const out = [];
    for (let n = min; n <= max; n++) out.push({ value: String(n), label: String(n) });
    return out;
  }

  function updateSubtotal(index, field, value) {
    const updated = [...subtotalsList.value];
    updated[index] = { ...updated[index], [field]: value };
    subtotalsList.value = updated;
  }

  function addSubtotal() {
    subtotalsList.value = [...subtotalsList.value, { label: "", codes: [] }];
  }

  function removeSubtotal(index) {
    subtotalsList.value = subtotalsList.value.filter((_, i) => i !== index);
  }

  return {
    isSubtotalsEligible,
    hasSubtotals,
    subtotalsList,
    subtotalAnswerOptions,
    updateSubtotal,
    addSubtotal,
    removeSubtotal,
  };
}
